/**
 * SelectedImportProvider — the app-wide "current import".
 *
 * Before this provider, `/research/ai-review` and `/research/backchannel-tree`
 * each kept their own `useState` for the import they operate on, so choosing
 * one on AI Review and then opening BCT meant finding and choosing it again.
 * Both pages now read and write this single value through
 * `<ImportSelectionBar>`.
 *
 * Persistence is `localStorage` (not `sessionStorage`, unlike
 * `PreviewProvider`): the selection is a working context the recruiter
 * expects to still be there after a reload or in a second tab, not a
 * per-session reveal flag. The stored value is validated on read
 * (`parseStoredSelectedImport`) — a partially-restored selection would render
 * `undefined` in the bar and confuse the per-page requirement predicates, so
 * anything unrecognized resolves to "nothing selected".
 *
 * SCOPE, deliberately: the selection is shared across PAGES and survives a
 * reload, and a NEWLY OPENED tab reads it at mount. Two tabs already open do
 * NOT track each other, and the last one to write wins for the next reader.
 *
 * That is a decision, not an oversight. A `storage`-event listener was built
 * and then removed: adopting a peer tab's write mid-session walks straight past
 * this feature's own guards — AI Review resets its run on any selection change,
 * so a peer write silently discarded an unapplied review that the "Discard
 * unapplied review?" dialog exists to protect, and it could cancel a BCT save
 * whose run the server had already accepted. Every attempt to hold the write
 * until it was safe produced a new way to lose someone's pick (a background
 * revalidation tick is indistinguishable from a deliberate choice at the
 * provider boundary). Live cross-tab convergence was never a requirement; it
 * cost two blocking defects, so it is out. Reintroduce it only with a visible
 * affordance ("another tab selected X — switch?"), never silently.
 *
 * Every storage call is wrapped: Safari private mode throws on `setItem`, and
 * a hardened browser can throw on `getItem` too. Failing there degrades to an
 * in-memory selection for the session rather than taking the page down.
 */
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import type { ImportSummary } from "@nu-assist/contracts";
import {
  SELECTED_IMPORT_STORAGE_KEY,
  type SelectedImport,
  clearStoredSelectedImport,
  parseStoredSelectedImport,
  toSelectedImport,
} from "./selectedImport";

// Re-exported so existing call sites (logout, session expiry) keep one import
// path even though the implementation now lives in the leaf module.
export { clearStoredSelectedImport };

interface SelectedImportContextValue {
  /** The current import, or `null` when nothing is selected. */
  selected: SelectedImport | null;
  /**
   * Commit a selection from a finder row. Projects the `ImportSummary` first —
   * this is the path a user click takes.
   */
  select: (item: ImportSummary) => void;
  /**
   * Commit an ALREADY-projected value. Used by the non-click paths that have
   * a `SelectedImport` in hand rather than a list row: deep-link adoption and
   * the revalidation refresh in `ImportSelectionBar`.
   */
  setSelected: (value: SelectedImport) => void;
  clear: () => void;
}

const SelectedImportContext = createContext<SelectedImportContextValue | null>(null);

function readStored(): SelectedImport | null {
  try {
    return parseStoredSelectedImport(window.localStorage.getItem(SELECTED_IMPORT_STORAGE_KEY));
  } catch {
    return null;
  }
}

function writeStored(value: SelectedImport | null): void {
  try {
    if (value === null) {
      window.localStorage.removeItem(SELECTED_IMPORT_STORAGE_KEY);
    } else {
      window.localStorage.setItem(SELECTED_IMPORT_STORAGE_KEY, JSON.stringify(value));
    }
  } catch {
    // Storage unavailable (private mode / quota) — the in-memory state below
    // is still authoritative for this session.
  }
}


export interface SelectedImportProviderProps {
  children: ReactNode;
  /**
   * Test-only seed. When provided it wins over whatever is in storage, the
   * same escape-hatch shape the pages use for their injected clients.
   */
  initialValue?: SelectedImport | null;
}

export function SelectedImportProvider({ children, initialValue }: SelectedImportProviderProps) {
  const [selected, setSelectedState] = useState<SelectedImport | null>(
    () => initialValue ?? readStored()
  );
  const setSelected = useCallback((value: SelectedImport) => {
    setSelectedState(value);
    writeStored(value);
  }, []);

  const select = useCallback(
    (item: ImportSummary) => {
      setSelected(toSelectedImport(item));
    },
    [setSelected]
  );

  const clear = useCallback(() => {
    setSelectedState(null);
    writeStored(null);
  }, []);

  const value = useMemo<SelectedImportContextValue>(
    () => ({ selected, select, setSelected, clear }),
    [selected, select, setSelected, clear]
  );

  return (
    <SelectedImportContext.Provider value={value}>{children}</SelectedImportContext.Provider>
  );
}

export function useSelectedImport(): SelectedImportContextValue {
  const ctx = useContext(SelectedImportContext);
  if (!ctx) {
    throw new Error("useSelectedImport must be used within a <SelectedImportProvider>");
  }
  return ctx;
}
